/**
 * ShareListMemberPicker — checklist of family members with toggles.
 * The owner is shown as pinned and cannot be deselected.
 *
 * Blocker (flagged 2026-04-22): FamilyMember records do not currently
 * carry a Supabase auth userId, so there is no way to add an invitable
 * participant that satisfies the task_lists RLS policy. The component
 * renders the empty state until the family-member → auth-user linkage
 * lands. The structural contract (visible/list/onSave/onDismiss) is
 * intact so the rest of the wiring can proceed.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TaskList } from '@/types/task-lists';

type ShareListMemberPickerProps = {
  visible: boolean;
  list: TaskList | null;
  onSave: (participantUserIds: string[]) => Promise<void>;
  onDismiss: () => void;
};

export function ShareListMemberPicker({
  visible,
  list,
  onSave,
  onDismiss,
}: ShareListMemberPickerProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const scale = useSharedValue(0.95);
  const opacity = useSharedValue(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      scale.value = withTiming(1, { duration: 150 });
      opacity.value = withTiming(1, { duration: 150 });
      setError(null);
    } else {
      scale.value = withTiming(0.95, { duration: 150 });
      opacity.value = withTiming(0, { duration: 150 });
    }
  }, [visible, scale, opacity]);

  const cardStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));
  const backdropStyle = useAnimatedStyle(() => ({ opacity: opacity.value * 0.5 }));

  const handleSave = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Until family members carry Supabase userIds, saving preserves
      // whatever the list already has.
      await onSave(list?.participantUserIds ?? []);
      onDismiss();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [list, onSave, onDismiss, submitting]);

  if (!list) return null;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onDismiss}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
      </Animated.View>
      <View style={styles.centered} pointerEvents="box-none">
        <Animated.View
          style={[styles.card, { backgroundColor: palette.cardBackground }, cardStyle]}
        >
          <Text style={[styles.title, { color: palette.text }]}>Share &quot;{list.name}&quot;</Text>
          <Text style={[styles.empty, { color: palette.textMuted }]}>
            Sharing with family members requires linked FimFam accounts. Once
            your family members sign in, they&apos;ll appear here to invite.
          </Text>

          {error && <Text style={[styles.error, { color: palette.danger }]}>{error}</Text>}

          <View style={styles.actionRow}>
            <TouchableOpacity onPress={onDismiss} style={styles.buttonGhost}>
              <Text style={[styles.buttonText, { color: palette.textMuted }]}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleSave}
              disabled={submitting}
              style={[
                styles.buttonPrimary,
                { backgroundColor: palette.tint, opacity: submitting ? 0.5 : 1 },
              ]}
            >
              <Text style={[styles.buttonText, { color: '#fff' }]}>Done</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: '#000' },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: {
    width: 320,
    padding: Spacing.lg,
    borderRadius: BorderRadius.lg,
    gap: Spacing.sm,
  },
  title: { fontSize: 17, fontWeight: '600' },
  empty: { fontSize: 14, paddingVertical: Spacing.md, lineHeight: 20 },
  error: { fontSize: 13 },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  buttonGhost: { paddingHorizontal: Spacing.md, paddingVertical: Spacing.sm },
  buttonPrimary: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.md,
  },
  buttonText: { fontSize: 15, fontWeight: '600' },
});
